import { BillableIcon, Currency, Field, Hours, Table } from '~/components';
import { TableBoxRowActions } from '~/components/table';
import { Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { weights } from '~/styles';
import { calculateRevenue, emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';
import { ProjectDrawerContext } from '../../ProjectDrawer';
import calculateServicesBudget from './calculateServicesBudget';

function BudgetMembersTable({ projectModel, onChange }) {
  const [editIndex, setEditIndex] = useState(null);

  const handleSave = (value) => {
    const members = projectModel.members.map((pm) =>
      pm.id === value.id
        ? emptyStringToNull({
            ...pm,
            estimatedBillableHours: value.estimatedBillableHours,
            estimatedNonBillableHours: value.estimatedNonBillableHours,
          })
        : pm,
    );

    setEditIndex(null);
    onChange(members);
  };

  const budget = calculateServicesBudget(projectModel);

  const currency = projectModel.currency;

  return (
    <Table small>
      <Table.BoxHeader>
        <Table.Column>Project Member</Table.Column>
        <Table.Column align="right" width="7.5rem" isVisible={projectModel.isBillable}>
          Billable
        </Table.Column>
        <Table.Column align="right" width="7.5rem">
          Non-billable
        </Table.Column>
        <Table.Column
          align="right"
          width="7.5rem"
          isVisible={projectModel.isBillable && projectModel.billingTypeId === 'tm'}>
          Rate
        </Table.Column>
        <Table.Column
          align="right"
          width="7.5rem"
          isVisible={projectModel.isBillable && projectModel.billingTypeId === 'tm'}>
          Revenue
        </Table.Column>
        <Table.BoxActionsColumn />
      </Table.BoxHeader>
      <Table.Body>
        {projectModel.members.length > 0 && (
          <>
            {projectModel.members.map((item, index) => (
              <BudgetMemberRow
                key={item.id}
                projectModel={projectModel}
                projectMember={item}
                disableActions={editIndex !== null}
                isEditing={editIndex === index}
                currency={currency}
                onEdit={() => setEditIndex(index)}
                onCancel={() => setEditIndex(null)}
                onSaved={handleSave}
              />
            ))}
            <Table.Row style={{ borderBottom: 'none', fontWeight: weights.bold, textTransform: 'uppercase' }}>
              <Table.Cell />
              <Table.Cell>
                <Hours value={budget.billableHours} />
              </Table.Cell>
              <Table.Cell>
                <Hours value={budget.nonBillableHours} />
              </Table.Cell>
              <Table.Cell>
                <Currency value={budget.effectiveRate} currency={currency} />
              </Table.Cell>
              <Table.Cell>
                <Currency value={budget.revenue} currency={currency} />
              </Table.Cell>
              <Table.Cell />
            </Table.Row>
          </>
        )}
      </Table.Body>
    </Table>
  );
}

function BudgetMemberRow({
  currency,
  projectModel,
  projectMember,
  disableActions,
  isEditing,
  onEdit,
  onCancel,
  onSaved,
}) {
  async function handleSubmit(values) {
    onSaved(values);
  }

  if (!isEditing)
    return (
      <BudgetMemberRowDetails
        projectModel={projectModel}
        projectMember={projectMember}
        disableActions={disableActions}
        currency={currency}
        onEdit={onEdit}
      />
    );

  return (
    <BudgetMemberRowForm
      projectModel={projectModel}
      projectMember={projectMember}
      currency={currency}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    />
  );
}

function BudgetMemberRowDetails({ currency, projectModel, projectMember, disableActions, onEdit }) {
  const { member, isBillable, rate, estimatedBillableHours, estimatedNonBillableHours, isActive } = projectMember;
  const isActuallyBillable = projectModel.isBillable && isBillable;
  const actualRate = isActuallyBillable ? rate : null;

  return (
    <Table.BoxRow isDisabled={!isActive}>
      <Table.Cell>
        <BillableIcon value={isActuallyBillable} />
        {member.name}
      </Table.Cell>
      <Table.Cell>
        <Hours value={isBillable ? estimatedBillableHours : null} />
      </Table.Cell>
      <Table.Cell>
        <Hours value={estimatedNonBillableHours} />
      </Table.Cell>
      <Table.Cell>
        <Currency value={actualRate} maximumFractionDigits={7} currency={currency} />
      </Table.Cell>
      <Table.Cell>
        <Currency value={calculateRevenue(estimatedBillableHours, actualRate)} currency={currency} />
      </Table.Cell>
      <TableBoxRowActions>
        <TableBoxRowActions.Edit disabled={disableActions} onClick={onEdit} />
      </TableBoxRowActions>
    </Table.BoxRow>
  );
}

function BudgetMemberRowForm({
  currency,
  projectModel,
  projectMember,
  projectMember: { id, member, rate, isBillable },
  onSubmit,
  onCancel,
}) {
  // Submit the row if the drawer's "Save" button is clicked
  const form = useRef();
  const { setForms } = useContext(ProjectDrawerContext);

  useEffect(() => {
    // Register the inline form
    setForms((forms) => [...forms, { id: 'budget_member', ref: form }]);

    return () => {
      // Unregister the inline form
      setForms((forms) => forms.filter((f) => f.id !== 'budget_member'));
    };
  }, [setForms]);

  const initialValues = mergeValues(
    {
      id,
      estimatedBillableHours: '',
      estimatedNonBillableHours: '',
    },
    projectMember,
  );

  const isActuallyBillable = projectModel.isBillable && isBillable;
  const actualRate = isActuallyBillable ? rate : null;

  return (
    <Formik
      innerRef={form}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={Yup.object().shape({
        estimatedBillableHours: Yup.number().label('Billable Hours').min(0).max(9999.99).nullable(),
        estimatedNonBillableHours: Yup.number().label('Non-Billable Hours').min(0).max(9999.99).nullable(),
      })}>
      {({ submitForm, values: { estimatedBillableHours } }) => {
        return (
          <Table.BoxRow focused onEnter={submitForm}>
            <Table.Cell>
              <BillableIcon value={isActuallyBillable} />
              {member.name}
            </Table.Cell>
            <Table.Cell>
              {isActuallyBillable ? (
                <Field.Number
                  autoFocus={isActuallyBillable}
                  name="estimatedBillableHours"
                  materialPlaceholder="Billable"
                  materialAlwaysVisible
                  min={0}
                  precision={2}
                />
              ) : (
                '-'
              )}
            </Table.Cell>
            <Table.Cell>
              <Field.Number
                autoFocus={!isActuallyBillable}
                name="estimatedNonBillableHours"
                materialPlaceholder="Non-Billable"
                materialAlwaysVisible
                min={0}
                precision={2}
              />
            </Table.Cell>
            <Table.Cell>
              <Currency value={actualRate} maximumFractionDigits={7} currency={currency} />
            </Table.Cell>
            <Table.Cell>
              <Currency value={calculateRevenue(estimatedBillableHours, actualRate)} currency={currency} />
            </Table.Cell>

            <TableBoxRowActions.Form onSubmit={submitForm} onCancel={onCancel} />
          </Table.BoxRow>
        );
      }}
    </Formik>
  );
}

export default BudgetMembersTable;
